import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Class, ClassSession } from '@/types/database';
import { calculateDistance } from './useLocationSearch';

interface ClassFilters {
  categoryId?: string;
  locationTypes?: string[];
  priceRange?: [number, number];
  dateRange?: [Date, Date];
  searchQuery?: string;
  instructorId?: string;
  coordinates?: { lat: number; lng: number };
  radiusMiles?: number;
  upcomingOnly?: boolean;
  enabled?: boolean;
}

export interface ClassWithDistance extends Class {
  distance?: number;
}

export function useClasses(filters?: ClassFilters) {
  return useQuery({
    queryKey: ['classes', filters],
    enabled: filters?.enabled !== false,
    queryFn: async () => {
      let query = supabase
        .from('classes')
        .select(`
          *,
          instructor:profiles_public!classes_instructor_id_fkey(id, full_name, bio, avatar_url, is_instructor),
          category:categories(*)
        `)
        .in('status', ['published', 'presell'])
        .order('class_date', { ascending: true });

      if (filters?.upcomingOnly) {
        query = query.gte('class_date', new Date().toISOString());
      }

      if (filters?.categoryId) {
        query = query.eq('category_id', filters.categoryId);
      }

      if (filters?.locationTypes && filters.locationTypes.length > 0) {
        query = query.in('location_type', filters.locationTypes);
      }

      if (filters?.priceRange) {
        query = query.gte('price', filters.priceRange[0]).lte('price', filters.priceRange[1]);
      }

      if (filters?.dateRange) {
        query = query
          .gte('class_date', filters.dateRange[0].toISOString())
          .lte('class_date', filters.dateRange[1].toISOString());
      }

      if (filters?.searchQuery?.trim()) {
        // Sanitize search query: allow only safe characters, limit length
        const sanitized = filters.searchQuery
          .trim()
          .slice(0, 100)
          // Allow only alphanumeric, spaces, hyphens, apostrophes
          .replace(/[^a-zA-Z0-9\s'-]/g, '')
          // Remove excessive whitespace
          .replace(/\s+/g, ' ')
          .trim();
        
        // Only search if we have meaningful content after sanitization
        if (sanitized.length >= 2) {
          query = query.or(`title.ilike.%${sanitized}%,description.ilike.%${sanitized}%`);
        }
      }

      if (filters?.instructorId) {
        query = query.eq('instructor_id', filters.instructorId);
      }

      const { data, error } = await query;
      if (error) throw error;

      let classes = data as ClassWithDistance[];

      // If location search is active, calculate distances and filter
      if (filters?.coordinates) {
        const { lat, lng } = filters.coordinates;
        const radiusMiles = filters.radiusMiles || 50;

        // Calculate distance for each class with coordinates
        classes = classes.map(c => {
          if (c.latitude && c.longitude) {
            return {
              ...c,
              distance: calculateDistance(lat, lng, c.latitude, c.longitude),
            };
          }
          // Virtual classes have no distance
          if (c.location_type === 'virtual') {
            return { ...c, distance: 0 };
          }
          return { ...c, distance: undefined };
        });

        // Filter classes within radius (include virtual classes)
        const withinRadius = classes.filter(
          c => c.location_type === 'virtual' || (c.distance !== undefined && c.distance <= radiusMiles)
        );

        if (withinRadius.length > 0) {
          // Sort by distance (virtual classes first, then by distance)
          return withinRadius.sort((a, b) => {
            if (a.location_type === 'virtual' && b.location_type !== 'virtual') return -1;
            if (a.location_type !== 'virtual' && b.location_type === 'virtual') return 1;
            return (a.distance || 0) - (b.distance || 0);
          });
        }

        // No classes within radius - return closest classes instead
        const classesWithDistance = classes.filter(c => c.distance !== undefined || c.location_type === 'virtual');
        return classesWithDistance.sort((a, b) => {
          if (a.location_type === 'virtual' && b.location_type !== 'virtual') return -1;
          if (a.location_type !== 'virtual' && b.location_type === 'virtual') return 1;
          return (a.distance || 0) - (b.distance || 0);
        }).slice(0, 10); // Return top 10 closest
      }

      return classes;
    },
  });
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useClass(classIdOrSlug: string | undefined) {
  return useQuery({
    queryKey: ['class', classIdOrSlug],
    queryFn: async () => {
      if (!classIdOrSlug) return null;

      const isUUID = UUID_REGEX.test(classIdOrSlug);
      const column = isUUID ? 'id' : 'slug';

      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          instructor:profiles_public!classes_instructor_id_fkey(id, full_name, bio, avatar_url, is_instructor),
          category:categories(*)
        `)
        .eq(column, classIdOrSlug)
        .maybeSingle();

      if (error) throw error;
      return data as Class | null;
    },
    enabled: !!classIdOrSlug,
  });
}

export function useFeaturedClasses() {
  return useQuery({
    queryKey: ['featured-classes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          instructor:profiles_public!classes_instructor_id_fkey(id, full_name, bio, avatar_url, is_instructor),
          category:categories(*)
        `)
        .in('status', ['published', 'presell'])
        .gte('class_date', new Date().toISOString())
        .limit(50);

      if (error) throw error;

      // Sort: real classes (with instructor) first, then dummy classes; within each group by class_date ascending
      const sorted = (data as Class[]).sort((a, b) => {
        const aReal = a.instructor_id ? 0 : 1;
        const bReal = b.instructor_id ? 0 : 1;
        if (aReal !== bReal) return aReal - bReal;
        return new Date(a.class_date).getTime() - new Date(b.class_date).getTime();
      });

      return sorted;
    },
  });
}

export function useClassSessions(classId: string | undefined) {
  return useQuery({
    queryKey: ['class-sessions', classId],
    queryFn: async () => {
      if (!classId) return [];
      const { data, error } = await supabase
        .from('class_sessions')
        .select('*')
        .eq('class_id', classId)
        .order('session_number', { ascending: true });
      if (error) throw error;
      return data as ClassSession[];
    },
    enabled: !!classId,
  });
}

export function useInstructorClasses(instructorId: string | undefined) {
  return useQuery({
    queryKey: ['instructor-classes', instructorId],
    queryFn: async () => {
      if (!instructorId) return [];

      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          category:categories(*)
        `)
        .eq('instructor_id', instructorId)
        .order('class_date', { ascending: false });

      if (error) throw error;
      return data as Class[];
    },
    enabled: !!instructorId,
  });
}

export function useCreateClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (classData: Omit<Class, 'id' | 'slug' | 'is_free' | 'created_at' | 'updated_at' | 'instructor' | 'category' | 'bookings_count' | 'average_rating' | 'reviews_count'>) => {
      const { data, error } = await supabase
        .from('classes')
        .insert(classData)
        .select()
        .single();

      if (error) {
        if (error.message?.includes('Active class limit reached')) {
          throw new Error('CLASS_LIMIT_REACHED');
        }
        throw error;
      }
      
      // Auto-geocode the class location in the background
      if (data && (classData.address || classData.city || classData.zip_code)) {
        supabase.functions.invoke('geocode-class', {
          body: {
            classId: data.id,
            address: classData.address,
            city: classData.city,
            zipCode: classData.zip_code,
          },
        }).then(({ data: geoData, error: geoError }) => {
          if (geoError) {
            console.warn('Geocoding failed:', geoError);
          } else if (geoData?.latitude && geoData?.longitude) {
            console.log('Class geocoded:', geoData.latitude, geoData.longitude);
          }
        }).catch(err => console.warn('Geocoding error:', err));
      }
      
      // Only send emails for published classes (not drafts)
      if (data.status === 'published') {
        // Send class creation confirmation email in the background
        supabase.functions.invoke('send-class-created-confirmation', {
          body: { classId: data.id },
        }).then(({ error: emailError }) => {
          if (emailError) {
            console.warn('Class creation confirmation email failed:', emailError);
          } else {
            console.log('Class creation confirmation email sent');
          }
        }).catch(err => console.warn('Class creation email error:', err));

        // Send announcement email to past students in the background
        supabase.functions.invoke('send-new-class-announcement', {
          body: { classId: data.id },
        }).then(({ error: announceError }) => {
          if (announceError) {
            console.warn('New class announcement email failed:', announceError);
          } else {
            console.log('New class announcement emails sent');
          }
        }).catch(err => console.warn('Announcement email error:', err));
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    },
  });
}

export function useUpdateClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ classId, updates }: { classId: string; updates: Partial<Class> }) => {
      const { data, error } = await supabase
        .from('classes')
        .update(updates)
        .eq('id', classId)
        .select()
        .single();

      if (error) {
        if (error.message?.includes('Active class limit reached')) {
          throw new Error('CLASS_LIMIT_REACHED');
        }
        throw error;
      }
      
      // Re-geocode if address/city/zip changed
      if (data && (updates.address !== undefined || updates.city !== undefined || updates.zip_code !== undefined)) {
        supabase.functions.invoke('geocode-class', {
          body: {
            classId: data.id,
            address: data.address,
            city: data.city,
            zipCode: data.zip_code,
          },
        }).then(({ data: geoData, error: geoError }) => {
          if (geoError) {
            console.warn('Geocoding failed:', geoError);
          } else if (geoData?.latitude && geoData?.longitude) {
            console.log('Class re-geocoded:', geoData.latitude, geoData.longitude);
          }
        }).catch(err => console.warn('Geocoding error:', err));
      }

      // Notify students when class is paused
      if (updates.status === 'paused') {
        supabase.functions.invoke('send-cancellation-email', {
          body: { classId, type: 'class_paused' },
        }).then(({ error: emailError }) => {
          if (emailError) console.warn('Pause notification failed:', emailError);
          else console.log('Pause notification sent');
        }).catch(err => console.warn('Pause notification error:', err));
      }
      
      return data;
    },
    onSuccess: (_, { classId }) => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({ queryKey: ['class', classId] });
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    },
  });
}

export function useDeleteClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (classId: string) => {
      // Refund paid students for upcoming classes (fire-and-forget)
      supabase.functions.invoke('refund-class-bookings', {
        body: { classId },
      }).then(({ data, error }) => {
        if (error) console.warn('Refund processing failed:', error);
        else console.log('Refund result:', data);
      }).catch(err => console.warn('Refund error:', err));

      // Notify all enrolled students about cancellation (fire-and-forget)
      supabase.functions.invoke('send-cancellation-email', {
        body: { classId, type: 'class_cancelled' },
      }).then(({ error }) => {
        if (error) console.warn('Cancellation notification failed:', error);
        else console.log('Cancellation notification sent');
      }).catch(err => console.warn('Cancellation notification error:', err));

      const { error } = await supabase
        .from('classes')
        .delete()
        .eq('id', classId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    },
  });
}
